import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import KpiGrid from '../../components/KpiGrid/KpiGrid';
import DynamicTable from '../../components/Table/Table';
import UploadModal from '../../components/Modals/Upload';
import { fetchMasterData, bulkUploadFile, fetchKpiCardData } from './services';
import CustomSkeleton from '../../components/Skeleton/CustomSkeleton';

const MasterDataTable = ({
  isBannerCloseClicked = false,
  setBannerCloseFalse,
  setUploadStatusMsg,
  isUploadDownloadDisabled,
  refreshData = 0,
}) => {
  const [data, setData] = useState([]);
  const [KpiCardData, setKpiCardData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isKpiCardLoading, setIsKpiCardLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);

  const columns = [
    {
      id: 'TPOSKUName',
      label: 'SKU NAME',
      type: 'text',
      category: 'Product Attributes',
      sticky: true,
      left: 56,
      showBorder: true,
      mappingCategory: 'attributesfinance'
    },
    {
      id: 'TPOSKU',
      label: 'TPOSKU',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributesfinance'
    },
    {
      id: 'UPC',
      label: 'UPC/GTIN',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributesfinance'
    },
    {
      id: 'TPOSegmentName',
      label: 'SEGMENT',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'MaterialGroupSubcategoryName',
      label: 'SUB CATEGORY',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'BrandStrategy',
      label: 'BRAND STRATEGY',
      type: 'text',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'UpdatedTPOSKUBrand',
      label: 'BRAND',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'UpdatedTPOSKUSubBrand',
      label: 'SUB BRAND',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'UpdatedTPOSKUFlavor',
      label: 'FLAVOUR',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'UpdatedTPOSKUSize',
      label: 'SIZE',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'PackCount',
      label: 'PACK COUNT',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'PackageTypeName',
      label: 'PACK TYPE',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    {
      id: 'Innovation',
      label: 'INNOVATION TYPE',
      type: 'dropdown',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'LaunchPeriodValue',
      label: 'LAUNCH PERIOD (YYYYPP)',
      type: 'text',
      editable: false,
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'DiscontinuedPeriodValue',
      label: 'DISCONTINUED PERIOD (YYYYPP)',
      type: 'text',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'HoldFlag',
      label: 'HOLD FLAG',
      type: 'dropdown',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'StrategicImportance',
      label: 'STRATEGIC IMPORTANCE',
      type: 'dropdown',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'SupplyChainComplexity',
      label: 'SUPPLY CHAIN COMPLEXITY',
      type: 'dropdown',
      editable: true,
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'ChannelImportance',
      label: 'CHANNEL IMPORTANCE',
      type: 'dropdown',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    {
      id: 'MustCarryList',
      label: 'MUST CARRY FLAG',
      type: 'dropdown',
      category: 'Lifecycle & Importance Factors',
      mappingCategory: 'attributes'
    },
    { id: 'GrossSales', label: 'GROSS SALES', type: 'number', mappingCategory: 'finance' },
    { id: 'GSvsLY', label: 'GS vs LY', type: 'number', mappingCategory: 'finance' },
    {
      id: 'OnTicketTradeSpend',
      label: 'ON-TICKET TRADE SPEND',
      type: 'number',
      mappingCategory: 'finance'
    },
    {
      id: 'NetInvoiceSales',
      label: 'NET INVOICE SALES',
      type: 'number',
      mappingCategory: 'finance'
    },
    { id: 'NSvsLY', label: 'NS vs LY', type: 'number', mappingCategory: 'finance' },
    {
      id: 'OffTicketTradeSpend',
      label: 'OFF-TICKET TRADE SPEND',
      type: 'number',
      mappingCategory: 'finance'
    },
    { id: 'CurrentYearRevenue', label: 'NET REVENUE', type: 'number', mappingCategory: 'finance' },
    { id: 'LastYearRevenue', label: 'LY REVENUE', type: 'number', mappingCategory: 'finance' },
    { id: 'RevenueGrowth', label: 'NR vs LY', type: 'number', mappingCategory: 'finance' },
    { id: 'COGS', label: 'COGS', type: 'number', mappingCategory: 'finance' },
    { id: 'GrossProfit', label: 'GROSS PROFIT', type: 'number', mappingCategory: 'finance' },
    { id: 'GPPercent', label: 'GP %', type: 'number', mappingCategory: 'finance' },
    { id: 'TotalSD', label: 'TOTAL S&D', type: 'number', mappingCategory: 'finance' },
    { id: 'NetProfit', label: 'NET PROFIT', type: 'number', mappingCategory: 'finance' },
    {
      id: 'CurrentYearProfitPercentage',
      label: 'NET PROFIT %',
      type: 'number',
      mappingCategory: 'finance'
    },
    { id: 'NPvsLY', label: 'NP vs LY', type: 'number', mappingCategory: 'finance' },
    { id: 'CurrentYearUnits', label: 'UNITS', type: 'number', mappingCategory: 'finance' },
    { id: 'LastYearUnits', label: 'LY UNITS', type: 'number', mappingCategory: 'finance' },
    { id: 'UnitGrowth', label: 'UNITS vs LY', type: 'number', mappingCategory: 'finance' },
    { id: 'Cases', label: 'CASES', type: 'number', mappingCategory: 'finance' },
    { id: 'CasesvsLY', label: 'CASES vs LY', type: 'number', mappingCategory: 'finance' },
    { id: 'Weight', label: 'WEIGHT', type: 'number', mappingCategory: 'finance' },
    { id: 'WeightvsLY', label: 'WEIGHT vs LY', type: 'number', mappingCategory: 'finance' },
    { id: 'TotalWaste', label: 'TOTAL WASTE ($)', type: 'number', mappingCategory: 'finance' },
    { id: 'CYWastePercentage', label: 'TOTAL WASTE %', type: 'number', mappingCategory: 'finance' },
    {
      id: 'FacilityCountL12W',
      label: 'FACILITY COUNT L12W',
      type: 'number',
      mappingCategory: 'finance'
    },
    {
      id: 'TPOSKUClassName',
      label: 'MANUFACTURING CLASS',
      type: 'text',
      category: 'Product Attributes',
      mappingCategory: 'attributes'
    },
    { id: 'LastUpdatedEmailIDWebapp', label: 'LAST MODIFIED BY', type: 'text' },
    { id: 'LastUpdatedTimestamp', label: 'LAST MODIFIED ON', type: 'date' }
  ];

  const getMasterData = async () => {
    setIsTableLoading(true);
    const allData = [];
    let pageNumber = 0;
    const dataLimitPerPage = 3500;
    let shouldContinue = true;

    try {
      while (shouldContinue) {
        const { data3 } = await fetchMasterData({
          BusinessUnitName: selectedBu,
          PageNumber: pageNumber,
          PageSize: dataLimitPerPage
        });

        allData.push(...data3);

        // Check if the data length is less than the limit, stop if so
        if (data3.length < dataLimitPerPage) {
          shouldContinue = false;
        } else {
          pageNumber++;
        }
      }

      setData(allData);
      return allData;
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const content = await getMasterData();
      // List of columns with new names to include in the download
      const columnMapping = {
        TPOSKUName: 'SKU NAME',
        TPOSKU: 'TPOSKU',
        UPC: 'UPC/GTIN',
        TPOSegmentName: 'SEGMENT',
        TPOSKUClassName: 'MANUFACTURING CLASS',
        UpdatedTPOSKUBrand: 'BRAND',
        UpdatedTPOSKUSubBrand: 'SUB BRAND',
        MaterialGroupSubcategoryName: 'SUB CATEGORY',
        UpdatedTPOSKUFlavor: 'FLAVOUR',
        UpdatedTPOSKUSize: 'SIZE',
        PackCount: 'PACK COUNT',
        PackageTypeName: 'PACK TYPE',
        Innovation: 'INNOVATION TYPE',
        LaunchPeriodValue: 'LAUNCH PERIOD (YYYYPP)',
        DiscontinuedPeriodValue: 'DISCONTINUED PERIOD (YYYYPP)',
        HoldFlag: 'HOLD FLAG',
        StrategicImportance: 'STRATEGIC IMPORTANCE',
        MustCarryList: 'MUST CARRY FLAG',
        BrandStrategy: 'BRAND STRATEGY',
        SupplyChainComplexity: 'SUPPLY CHAIN COMPLEXITY',
        ChannelImportance: 'CHANNEL IMPORTANCE',
        GrossSales: 'GROSS SALES',
        GSvsLY: 'GS vs LY',
        OnTicketTradeSpend: 'ON-TICKET TRADE SPEND',
        NetInvoiceSales: 'NET INVOICE SALES',
        NSvsLY: 'NS vs LY',
        OffTicketTradeSpend: 'OFF-TICKET TRADE SPEND',
        CurrentYearRevenue: 'NET REVENUE',
        LastYearRevenue: 'LY REVENUE',
        RevenueGrowth: 'NR vs LY',
        COGS: 'COGS',
        GrossProfit: 'GROSS PROFIT',
        GPPercent: 'GP %',
        TotalSD: 'TOTAL S&D',
        NetProfit: 'NET PROFIT',
        CurrentYearProfitPercentage: 'NET PROFIT %',
        NPvsLY: 'NP vs LY',
        CurrentYearUnits: 'UNITS',
        LastYearUnits: 'LY UNITS',
        UnitGrowth: 'UNITS vs LY',
        Cases: 'CASES',
        CasesvsLY: 'CASES vs LY',
        Weight: 'WEIGHT',
        WeightvsLY: 'WEIGHT vs LY',
        TotalWaste: 'TOTAL WASTE ($)',
        CYWastePercentage: 'TOTAL WASTE %',
        FacilityCount: 'FACILITY COUNT',
        FacilityCountL12W: 'FACILITY COUNT L12W',
        LastUpdatedEmailIDWebapp: 'LAST MODIFIED BY',
        LastUpdatedTimestamp: 'LAST MODIFIED ON',
        Status: 'STATUS',
        DeleteFlag: 'DELETE FLAG',
        BuTpoSkuKey: 'BUTPOSKUKEY'
      };

      // Transform content to only include specified columns and rename them
      const transformedContent = content.map((row) => {
        const transformedRow = {};
        Object.keys(columnMapping).forEach((key) => {
          if (key) {
            transformedRow[columnMapping[key]] = row[key] !== undefined ? row[key] : '';
          }
        });
        return transformedRow;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(transformedContent);

      //Create static sheet with editable columns info
      const editableColumns = [
        {
          'Column Names that can be edited': 'Validation/Possible accepted values',
          SEGMENT: 'Text Input',
          'SUB CATEGORY': 'Text Input',
          'INNOVATION TYPE':
            'Expected Values:\n1) LTO\n2) Refresh\n3) Reframe\n4) Breakthrough\n5) New Product',
          // 'LAUNCH PERIOD (YYYYPP)':
          //   'YYYY should be between 2000-2100 and PP should be between 01-13',
          'DISCONTINUED PERIOD (YYYYPP)':
            'No Restriction on YYYY whereas PP should be between 01-13',
          'HOLD FLAG': 'Expected Value:\n1) Yes\n2) No',
          'STRATEGIC IMPORTANCE': 'Expected Values:\n1) High\n2) Medium\n3) Low',
          'MUST CARRY FLAG': 'Expected Value:\n1) Must Carry',
          'BRAND STRATEGY': 'Text Input',
          'SUPPLY CHAIN COMPLEXITY': 'Expected Values:\n1) High\n2) Medium\n3) Low',
          'CHANNEL IMPORTANCE': 'Expected Values:\n1) High\n2) Medium\n3) Low'
        }
      ];

      //Create the worksheet from the editableColumns data
      const wsEditable = XLSX.utils.json_to_sheet(editableColumns, { cellStyles: true });

      //Now apply wrapText to all cells in the editableColumns data that have multi-line text
      Object.keys(editableColumns[0]).forEach((columnName, colIndex) => {
        // Apply wrapText style to all cells in the second row (for this example, A2, B2, etc.)
        const cell = wsEditable[XLSX.utils.encode_cell({ r: 1, c: colIndex })]; // A2, B2, etc.
        if (cell) {
          cell.s = cell.s || {}; // Ensure we don't override existing styles
          cell.s.alignment = { wrapText: true };
        }
      });

    //  Now you can append this sheet to your workbook and write the file as you normally would
      
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.utils.book_append_sheet(wb, wsEditable, 'Info-editable columns');
      XLSX.writeFile(wb, `${selectedBu}_PortfolioMaster.xlsx`);

      // console.log('Editable Columns:', editableColumns);
      // console.log('Worksheet object:', wsEditable);
    
     
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

            

  const handleBulkUpload = async (formData, onUploadProgress) => {
    if (formData) {
      await bulkUploadFile(formData, onUploadProgress)
        .then((res) => {
          setUploadStatusMsg(res);
        })
        .catch((error) => {
          setUploadStatusMsg(error);
        })
        .finally(() => {
          setIsUploadModalOpen(false);
        });
    }
  };

  const convertStrToNum = (data) => {
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (typeof item[key] === 'string' && !isNaN(item[key])) {
          const num = parseFloat(item[key]);
          if (num >= 1000000) {
            item[key] = (num / 1000000).toFixed(1) + ' M';
          } else if (num >= 1000) {
            item[key] = (num / 1000).toFixed(1) + ' K';
          } else {
            item[key] = num;
          }
        }
      });
    });
  };

  const getKpiCardData = async () => {
    setIsTableLoading(true);
    setIsKpiCardLoading(true);
    try {
      const response = await fetchKpiCardData(selectedBu);
      convertStrToNum(response);
      setKpiCardData(response);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsKpiCardLoading(false);
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBu) {
      getMasterData();
      getKpiCardData();
    }
  }, [selectedBu, refreshData]);

  useEffect(() => {
    if (isBannerCloseClicked) {
      getMasterData();
      getKpiCardData();
      setBannerCloseFalse();
    }
  }, [isBannerCloseClicked]);

  return (
    <>
      {isKpiCardLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px 24px',
            borderRadius: '8px',
            border: '1px solid #DBEAFE',
            backgroundColor: '#F8FAFC',
            width: '100%',
            height: '200px'
          }}>
          <Box // Use Box component for styling
            sx={{
              display: 'flex',
              justifyContent: 'center', // Center horizontally
              alignItems: 'center', // Center vertically
              height: '100vh' // Take full viewport height
            }}>
            <CustomSkeleton data-testid="custom-skeleton" />
          </Box>
        </Box>
      ) : (
        <>
          {KpiCardData && KpiCardData.length > 0 ? (
            <>
              <KpiGrid metricsData={KpiCardData} width="100%" selectedPage={'masterData'} />
            </>
          ) : (
            <></>
          )}
        </>
      )}

      <Box
        component="section"
        flexWrap="wrap"
        sx={{
          p: ['masterData', 'materialSkuMaster'].includes('masterData') ? 2 : 0,
          borderRadius: '8px',
          backgroundColor: ['masterData', 'materialSkuMaster'].includes('masterData')
            ? '#FBFCFE'
            : '',
          boxShadow: ['masterData', 'materialSkuMaster'].includes('masterData')
            ? '4px 0px 24px -2px #005CBC14'
            : ''
        }}>
        <DynamicTable
          originalColumns={columns}
          originalData={data}
          tableName="Master Data Table"
          isTableLoading={isTableLoading}
          isColumnVisibleFilter
          isColumnFilter // hiding column level filter
          isBulkUpload
          downloadExcel
          isGlobalFilter
          density="small"
          handleDownloadData={handleDownload}
          onBulkUploadModalOpen={() => setIsUploadModalOpen(true)}
          isUploadDownloadDisabled={isUploadDownloadDisabled}
        />
        {isUploadModalOpen && (
          <UploadModal
            open={isUploadModalOpen}
            handleOpen={() => setIsUploadModalOpen(true)}
            handleClose={() => setIsUploadModalOpen(false)}
            onUploadFile={handleBulkUpload}
            // file={file}
            mode="single"
            pageId={'BulkUpload'}
          />
        )}
      </Box>
    </>
  );
};

export default MasterDataTable;
